import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  CssBaseline,
  Stack,
  Typography,
} from "@mui/joy";
import {
  CanvasRenderer,
  EmbeddedCanvasRenderer,
} from "../components/canvasBuilder";
import { CanvasRuntimeProvider } from "../components/canvasBuilder/CanvasRuntimeContext";
import { CanvasThemeScope } from "../theme/CanvasThemeScope";
import type {
  RenderableElement,
  FieldState,
} from "../components/canvasBuilder";
import type { CanvasElement } from "../types";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { buildAuthReturnUrl } from "../../../utils/authReturnTarget";
import {
  isWritableAccess,
  visibleRuntimeElements,
} from "./runtimeElementAccess";

const DEFAULT_RUNTIME_GRID_CONFIG = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 3,
};

interface PublicCanvasData {
  id: string;
  workspace_id: string;
  name: string;
  description?: string | null;
  grid_columns?: number;
  grid_row_height?: number;
  grid_gap?: number;
  theme_config?: unknown;
  color?: string | null;
  elements?: unknown[];
  embeddings?: unknown[];
}

const buildRenderableElements = (
  canvas: PublicCanvasData | null,
): RenderableElement[] => {
  const rawElements = visibleRuntimeElements((canvas?.elements || []) as any[]);
  const rawEmbeddings = (canvas?.embeddings || []) as any[];
  const embeddingByElementId = new Map<string, any>();
  for (const embedding of rawEmbeddings) {
    const elementId = embedding?.element_id || embedding?.elementId;
    if (elementId) embeddingByElementId.set(String(elementId), embedding);
  }

  return rawElements.map((element, index) => {
    const dataBinding = element.data_binding
      ? {
          data_source_id:
            element.data_binding.data_source_alias ||
            element.data_binding.data_source_id,
          field_path: element.data_binding.field_path,
        }
      : undefined;
    const type = (element.element_type || element.type || "").toLowerCase();
    const baseProps = element.config_json || element.props || {};
    const embedding = element.id
      ? embeddingByElementId.get(String(element.id))
      : undefined;
    const props =
      type === "embedded_canvas" && embedding
        ? {
            childCanvasId: embedding.child_canvas_id || embedding.childCanvasId,
            parentDataSourceAlias:
              embedding.parent_data_source_alias ||
              embedding.parentDataSourceAlias,
            parentKeyField:
              embedding.parent_key_field || embedding.parentKeyField,
            childDataSourceAlias:
              embedding.child_data_source_alias ||
              embedding.childDataSourceAlias,
            childFilterField:
              embedding.child_filter_field || embedding.childFilterField,
            ...(baseProps || {}),
          }
        : baseProps || {};

    return {
      id: element.id || `el-${index}`,
      type,
      effectiveAccess: element.effective_access,
      variant: props.fieldType || (props.variant as string),
      props,
      gridColumn: element.grid_column || 1,
      gridRow: element.grid_row || index + 1,
      columnSpan: element.column_span || 12,
      rowSpan: element.row_span || 1,
      data_binding: dataBinding,
      parentElementId: element.parent_element_id || undefined,
      parentPanelId: element.parent_panel_id || undefined,
      displayOrder: element.sort_order ?? element.display_order ?? index,
    };
  });
};

const PublicCanvasViewer: React.FC = () => {
  const { canvasId } = useParams<{ canvasId: string }>();
  const [canvas, setCanvas] = useState<PublicCanvasData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  useEffect(() => {
    if (!canvasId) {
      setError("Invalid canvas URL");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCanvas = async () => {
      setLoading(true);
      setError(null);

      try {
        const url = `/api/public/canvases/${encodeURIComponent(canvasId)}`;
        const body = (await authFetchRef.current(
          url,
          "fetch published canvas",
        )) as PublicCanvasData;
        if (cancelled) return;
        setCanvas(body);
      } catch (err) {
        if (!cancelled) {
          const status = (err as Error & { status?: number }).status;
          if (status === 401) {
            window.location.replace(buildAuthReturnUrl());
            return;
          }
          setCanvas(null);
          setError(
            status === 403 || status === 404 || status === 410
              ? "Canvas not found or not available."
              : err instanceof Error
                ? err.message
                : "Failed to load canvas",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCanvas();

    return () => {
      cancelled = true;
    };
  }, [canvasId]);

  const gridConfig = useMemo(() => {
    if (!canvas) return DEFAULT_RUNTIME_GRID_CONFIG;
    return {
      columns: canvas.grid_columns || DEFAULT_RUNTIME_GRID_CONFIG.columns,
      rowHeight:
        canvas.grid_row_height || DEFAULT_RUNTIME_GRID_CONFIG.rowHeight,
      gap: canvas.grid_gap ?? DEFAULT_RUNTIME_GRID_CONFIG.gap,
      minRows: DEFAULT_RUNTIME_GRID_CONFIG.minRows,
    };
  }, [canvas]);

  const renderableElements = useMemo(
    () => buildRenderableElements(canvas),
    [canvas],
  );

  const fieldState = useMemo<Record<string, FieldState>>(() => ({}), []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          Loading canvas...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 3,
        }}
      >
        <Alert color="danger" variant="soft" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!canvas) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 3,
        }}
      >
        <Alert color="warning" variant="soft" sx={{ maxWidth: 500 }}>
          Canvas not found or not available.
        </Alert>
      </Box>
    );
  }

  if (renderableElements.length === 0) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.level1" }}>
        <CssBaseline />
        <Box
          sx={{
            p: 2,
            bgcolor: "background.surface",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography level="h4">{canvas.name}</Typography>
        </Box>
        <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
          <Alert color="neutral" variant="soft">
            <Stack spacing={1}>
              <Typography level="title-md">
                Nothing has been published yet.
              </Typography>
              <Typography level="body-sm">
                This canvas is live, but it does not contain any runtime content
                yet.
              </Typography>
            </Stack>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.level1" }}>
      <CssBaseline />
      <Box
        sx={{
          p: 2,
          bgcolor: "background.surface",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography level="h4">{canvas.name}</Typography>
      </Box>
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <CanvasRuntimeProvider
          value={{
            workspaceId: canvas.workspace_id,
            canvasId: canvasId || null,
            rowId: null,
            rowData: {},
          }}
        >
          <CanvasThemeScope
            themeConfig={canvas.theme_config}
            color={canvas.color || undefined}
          >
            <CanvasRenderer
              elements={renderableElements}
              config={gridConfig}
              mode="runtime"
              responsive
              fieldState={fieldState}
              renderEmbeddedCanvas={(element) => {
                const embeddedElement: CanvasElement = {
                  id: element.id,
                  type: element.type as CanvasElement["type"],
                  props: element.props,
                  data_binding: element.data_binding as any,
                  layout: {
                    gridColumn: element.gridColumn,
                    gridRow: element.gridRow,
                    columnSpan: element.columnSpan,
                    rowSpan: element.rowSpan,
                  },
                  parentElementId: element.parentElementId,
                  parentPanelId: element.parentPanelId,
                  displayOrder: element.displayOrder,
                };

                return (
                  <EmbeddedCanvasRenderer
                    element={embeddedElement}
                    parentData={{}}
                    workspaceId={canvas.workspace_id}
                    parentCanvasId={canvasId}
                    allowCreate={isWritableAccess(element.effectiveAccess)}
                    allowEdit={isWritableAccess(element.effectiveAccess)}
                    allowDelete={isWritableAccess(element.effectiveAccess)}
                  />
                );
              }}
              title={canvas.name}
              description={canvas.description || undefined}
            />
          </CanvasThemeScope>
        </CanvasRuntimeProvider>
      </Box>
    </Box>
  );
};

export default PublicCanvasViewer;
